import { openDB, IDBPDatabase } from 'idb';

export interface SavedEpubFile {
  id: string;
  fileName: string;
  title: string;
  size: number;
  addedAt: number;
  fileData: ArrayBuffer;
}

const DB_NAME = 'libritui_beta_epub_db';
const STORE_NAME = 'epub_files';
const DB_VERSION = 1;

class BetaEpubStorageService {
  private dbPromise: Promise<IDBPDatabase<any>> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initDb();
    }
  }

  private initDb() {
    this.dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('by_addedAt', 'addedAt');
        }
      },
    });
  }

  async getAllEpubs(): Promise<Omit<SavedEpubFile, 'fileData'>[]> {
    if (!this.dbPromise) return [];
    try {
      const db = await this.dbPromise;
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.store;
      const all = await store.getAll();
      return all.map(({ fileData, ...rest }) => rest).sort((a, b) => b.addedAt - a.addedAt);
    } catch (err) {
      console.error('Error fetching EPUB list from IndexedDB:', err);
      return [];
    }
  }

  async getEpub(id: string): Promise<SavedEpubFile | null> {
    if (!this.dbPromise) return null;
    try {
      const db = await this.dbPromise;
      const file = await db.get(STORE_NAME, id);
      return file || null;
    } catch (err) {
      console.error(`Error loading EPUB ${id} from IndexedDB:`, err);
      return null;
    }
  }

  async saveEpub(fileName: string, title: string, fileData: ArrayBuffer): Promise<SavedEpubFile> {
    if (!this.dbPromise) throw new Error('IndexedDB unavailable');
    const db = await this.dbPromise;
    const id = `epub_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const record: SavedEpubFile = {
      id,
      fileName,
      title: title || fileName.replace(/\.epub$/i, ''),
      size: fileData.byteLength,
      addedAt: Date.now(),
      fileData,
    };
    await db.put(STORE_NAME, record);
    return record;
  }

  async deleteEpub(id: string): Promise<void> {
    if (!this.dbPromise) return;
    try {
      const db = await this.dbPromise;
      await db.delete(STORE_NAME, id);
    } catch (err) {
      console.error(`Error deleting EPUB ${id} from IndexedDB:`, err);
    }
  }
}

export const betaEpubStorage = new BetaEpubStorageService();
